// Angular
import { Component, OnInit, OnDestroy } from '@angular/core';
// Material Dialog
import { MatDialog, MatDialogConfig } from '@angular/material';
import { DevRolePasswordDialogComponent } from '../../../../core/_components/dev-role-switcher/dev-role-password-dialog.component';
// Services
import { DevRoleSwitcherAuthService } from '../../../../core/_services/dev-role-switcher-auth.service';
import { DevImpersonationService } from '../../../../core/_services/dev-impersonation.service';
import { DemoModeService } from '../../../../core/demo-mode';
import { ViewAsScopeService } from '../../../../core/view-as-scope';
// Environment
import { environment } from '../../../../../environments/environment';
// Constants
import { DEV_SWITCHER_REQUIRED_CLICKS, DEV_SWITCHER_CLICK_RESET_TIMEOUT } from '../../../../core/_config/dev.constants';

@Component({
	selector: 'kt-topbar',
	templateUrl: './topbar.component.html',
	styleUrls: ['./topbar.component.scss'],
})
export class TopbarComponent implements OnInit, OnDestroy {
	private clickCount = 0;
	private readonly REQUIRED_CLICKS = DEV_SWITCHER_REQUIRED_CLICKS;
	private readonly CLICK_RESET_TIMEOUT = DEV_SWITCHER_CLICK_RESET_TIMEOUT;
	private clickTimeout: any;

	// Expose services to template
	devSwitcherAuthService = this._devSwitcherAuthService;

	get isSuperAdmin(): boolean {
		return this.roleOverrideService.getOriginalRole() === 'sadmin';
	}

	constructor(
		private dialog: MatDialog,
		private _devSwitcherAuthService: DevRoleSwitcherAuthService,
		private roleOverrideService: DevImpersonationService,
		public viewAsScopeService: ViewAsScopeService,
		public demoModeService: DemoModeService
	) {}

	ngOnInit(): void {
		// Component initialization
	}

	/**
	 * Determine if trigger should be shown
	 */
	shouldShowTrigger(): boolean {
		// Check if authenticated first
		if (this.devSwitcherAuthService.isAuthenticated()) {
			return false;
		}

		// Check if not production
		if (environment.production) {
			return false;
		}

		// Only show trigger for super admins
		// Get original user role (not overridden) to check actual permissions
		const originalRole = this.roleOverrideService.getOriginalRole();
		return originalRole === 'sadmin';
	}

	ngOnDestroy(): void {
		if (this.clickTimeout) {
			clearTimeout(this.clickTimeout);
		}
	}

	/**
	 * Handle click on dev switcher trigger
	 */
	onDevSwitcherTriggerClick(): void {
		// Only track clicks if not production
		if (environment.production) {
			return;
		}

		// If already authenticated, don't track clicks
		if (this.devSwitcherAuthService.isAuthenticated()) {
			return;
		}

		this.clickCount++;

		// Clear existing timeout
		if (this.clickTimeout) {
			clearTimeout(this.clickTimeout);
		}

		// Reset click count after timeout
		this.clickTimeout = setTimeout(() => {
			this.clickCount = 0;
		}, this.CLICK_RESET_TIMEOUT);

		// If we've reached the required number of clicks, show password dialog
		if (this.clickCount >= this.REQUIRED_CLICKS) {
			this.clickCount = 0;
			if (this.clickTimeout) {
				clearTimeout(this.clickTimeout);
			}
			this.showPasswordDialog();
		}
	}


	/**
	 * Show password dialog
	 */
	private showPasswordDialog(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.width = '400px';
		dialogConfig.disableClose = true;

		const dialogRef = this.dialog.open(DevRolePasswordDialogComponent, dialogConfig);

		dialogRef.afterClosed().subscribe(result => {
			if (result === true) {
				// Password was correct, store authentication state
				this.devSwitcherAuthService.setAuthenticated(true);
			}
		});
	}

}
